import { createContext, useContext, useEffect, useState } from 'react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// The backend counts devices by this id, so it is pinned in localStorage instead of being recomputed at every start:
// FingerprintJS only promises a stable visitorId within the same minor version, and browser or OS updates can change
// it too. A new id is seeded with the visitorId, so browsers already registered with it keep their device.
const DEVICE_ID_KEY = 'ps-device-id';

const DeviceIdContext = createContext<string | undefined>(undefined);

let fingerprint: string | undefined;
let fingerprintPromise: Promise<string | undefined> | undefined;

// FingerprintJS visitorId of this app load, for telemetry only (undefined until computed or if FingerprintJS failed).
export function getFingerprint() {
  return fingerprint;
}

function loadFingerprint() {
  if (!fingerprintPromise) {
    fingerprintPromise = FingerprintJS.load()
      .then((fp) => fp.get())
      .then((result) => {
        fingerprint = result.visitorId;
        return fingerprint;
      })
      .catch(() => undefined);
  }
  return fingerprintPromise;
}

function readStoredDeviceId() {
  try {
    return localStorage.getItem(DEVICE_ID_KEY) || undefined;
  } catch (e) {
    return undefined;
  }
}

function storeDeviceId(deviceId: string) {
  try {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  } catch (e) {}
}

function randomUUID() {
  // crypto.randomUUID is missing in older Safari and in non-secure contexts (and in the TS 4.5 DOM lib)
  const webCrypto: Crypto & { randomUUID?: () => string } = window.crypto;
  if (typeof webCrypto.randomUUID === 'function') return webCrypto.randomUUID();
  const bytes = webCrypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // RFC 4122 variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function DeviceIdProvider({ children }: any) {
  // a stored id is provided right away, without waiting for FingerprintJS
  const [deviceId, setDeviceId] = useState(readStoredDeviceId);

  useEffect(() => {
    loadFingerprint().then((visitorId) => {
      // read again: another tab may have stored an id in the meantime
      const storedDeviceId = readStoredDeviceId();
      const newDeviceId = storedDeviceId ?? visitorId ?? randomUUID();
      if (!storedDeviceId) storeDeviceId(newDeviceId);
      // once provided, the id never changes during the session
      setDeviceId((current) => current ?? newDeviceId);
    });
  }, []);

  return <DeviceIdContext.Provider value={deviceId}>{children}</DeviceIdContext.Provider>;
}

export function useDeviceId() {
  return useContext(DeviceIdContext);
}
